<div class="card">
  <div class="tw-flex tw-items-center tw-gap-2 tw-mb-3">
    <i class="pi pi-sliders-h tw-text-xl"></i>
    <h5 class="tw-m-0">Bulk Price Wizard</h5>
  </div>

  <p-steps [model]="steps" [activeIndex]="step" [readonly]="true" styleClass="tw-mb-4"></p-steps>

  @if (loading) {
  <div class="tw-flex tw-justify-center tw-py-10">
    <p-progressSpinner strokeWidth="4" [style]="{ width: '3rem', height: '3rem' }"></p-progressSpinner>
  </div>
  } @else {

  <!-- STEP 1: CONFIGURE ------------------------------------------------------>
  @if (step === 0) {
  <div class="p-fluid tw-max-w-3xl">
    <div class="field">
      <label class="tw-font-semibold">What do you want to do?</label>
      <p-selectButton [options]="directionOptions" [(ngModel)]="direction" optionLabel="label" optionValue="value">
        <ng-template let-option pTemplate="item">
          <i [class]="option.icon" class="tw-mr-2"></i>{{ option.label }}
        </ng-template>
      </p-selectButton>
    </div>

    <div class="field">
      <label htmlFor="percent" class="tw-font-semibold">Percentage</label>
      <p-inputNumber inputId="percent" [(ngModel)]="percent" [min]="0" [max]="1000" suffix=" %" [showButtons]="true"
        [step]="1"></p-inputNumber>
      <small class="tw-text-gray-500">
        All selected prices will be {{ direction === 'decrease' ? 'decreased' : 'increased' }} by {{ percent || 0 }}%
        (×{{ factor | number:'1.2-2' }}).
      </small>
    </div>

    <div class="field">
      <label class="tw-font-semibold">Which prices should change?</label>
      <div class="tw-flex tw-flex-col tw-gap-3 tw-mt-2">
        @for (cat of categories; track cat.key) {
        <div class="tw-flex tw-items-start tw-gap-2">
          <p-checkbox [binary]="true" [inputId]="cat.key" [(ngModel)]="cat.enabled"></p-checkbox>
          <label [htmlFor]="cat.key" class="tw-cursor-pointer tw-leading-tight">
            <span class="tw-font-medium">{{ cat.label }}</span><br />
            <small class="tw-text-gray-500">{{ cat.hint }}</small>
          </label>
        </div>
        }
      </div>
    </div>

    <div class="field tw-mt-2">
      <div class="tw-flex tw-items-center tw-gap-2">
        <p-checkbox [binary]="true" inputId="round" [(ngModel)]="round"></p-checkbox>
        <label htmlFor="round" class="tw-cursor-pointer tw-font-semibold">Round the new prices</label>
      </div>
      @if (round) {
      <div class="tw-mt-2 tw-max-w-xs">
        <p-dropdown [options]="roundModes" [(ngModel)]="roundMode" optionLabel="label" optionValue="value"
          appendTo="body"></p-dropdown>
      </div>
      }
    </div>

    <div class="tw-flex tw-justify-end tw-gap-2 tw-mt-4">
      <p-button label="Preview changes" icon="pi pi-arrow-right" iconPos="right" [disabled]="!canContinue"
        (click)="goToPreview()"></p-button>
    </div>
  </div>
  }

  <!-- STEP 2: PREVIEW -------------------------------------------------------->
  @if (step === 1) {
  @if (!entries.length) {
  <div class="tw-flex tw-items-center tw-gap-2 tw-p-3 tw-mb-3 tw-rounded-lg tw-bg-yellow-50 tw-text-yellow-800">
    <i class="pi pi-exclamation-triangle"></i>
    No matching prices were found for your selection.
  </div>
  } @else {
  <div class="tw-flex tw-flex-wrap tw-gap-2 tw-items-center tw-mb-3">
    <span class="tw-font-semibold">{{ entries.length }}</span> price(s) will be
    <span class="tw-font-semibold">{{ direction === 'decrease' ? 'decreased' : 'increased' }}</span>
    by <span class="tw-font-semibold">{{ percent }}%</span>.
    <span class="tw-text-gray-500" *ngIf="round">Rounded to: {{ roundMode }}</span>
  </div>

  <p-table [value]="entries" [paginator]="entries.length > 25" [rows]="25" [rowsPerPageOptions]="[25, 50, 100, 500]"
    styleClass="p-datatable-sm p-datatable-gridlines" responsiveLayout="scroll" [scrollable]="true"
    scrollHeight="50vh">
    <ng-template pTemplate="header">
      <tr>
        <th>Name</th>
        <th>Type</th>
        <th>Price</th>
        <th class="tw-text-right">Before</th>
        <th class="tw-text-right">After</th>
      </tr>
    </ng-template>
    <ng-template pTemplate="body" let-entry>
      <tr>
        <td>{{ entry.group }}</td>
        <td><span class="tw-text-gray-500">{{ entry.categoryLabel }}</span></td>
        <td>{{ entry.label }}</td>
        <td class="tw-text-right">{{ currency }}{{ entry.oldValue | number:'1.2-2' }}</td>
        <td class="tw-text-right tw-font-semibold"
          [class.tw-text-green-600]="entry.newValue > entry.oldValue"
          [class.tw-text-red-600]="entry.newValue < entry.oldValue">
          {{ currency }}{{ entry.newValue | number:'1.2-2' }}
        </td>
      </tr>
    </ng-template>
  </p-table>
  }

  <div class="tw-flex tw-justify-between tw-gap-2 tw-mt-4">
    <p-button label="Back" icon="pi pi-arrow-left" [outlined]="true" (click)="backToConfigure()"
      [disabled]="applying"></p-button>
    <p-button label="Apply changes" icon="pi pi-check" severity="success" [disabled]="!entries.length || applying"
      [loading]="applying" (click)="apply()"></p-button>
  </div>
  }

  <!-- STEP 3: DONE ----------------------------------------------------------->
  @if (step === 2) {
  <div class="tw-flex tw-flex-col tw-items-center tw-text-center tw-py-10 tw-gap-3">
    <i class="pi pi-check-circle tw-text-green-500" style="font-size: 3rem;"></i>
    <h5 class="tw-m-0">Prices updated successfully</h5>
    <p class="tw-text-gray-500 tw-m-0">{{ entries.length }} price(s) were updated.</p>
    <div class="tw-flex tw-gap-2 tw-mt-2">
      <p-button label="Run again" icon="pi pi-refresh" [outlined]="true" (click)="reset()"></p-button>
      <p-button label="Back to menu" icon="pi pi-list"
        [routerLink]="[basePath, 'menu', businessId, 'item']"></p-button>
    </div>
  </div>
  }

  }
</div>
